import mixpanel from '@disco/api/mixpanel'
import { AUDIO_FILE } from '@disco/constants/FileTypes'
import { TRACK } from '@disco/constants/ItemTypes'
import { getNounForExt } from '@disco/utils/file-utils'
import { getTrackStatusDetails } from '@disco/utils/tracks'

import React from 'react'
import type { TrackTextSettings } from 'types/business'
import type { Track } from 'types/track'
import ColorIndicators from 'components/common/color-indicators'
import WatermarkingStatusFlag from 'components/explorer/watermarking-status-flag'
import Flex from 'components/ui/flex'
import PreRelease from 'components/ui/pre-release'
import TextOverflow from 'components/ui/text-overflow'
export default function getTrackStatus(
  track: Track,
  options: {
    showWatermarking?: boolean
    textSettings?: TrackTextSettings
  } = {},
): {
  bar: React.ReactNode
  hasWaveform: boolean
  isOpenable: boolean
  isPlayable: boolean
  isPreviewable: boolean
  isTemp: boolean
  line1: string
  line2: React.ReactNode
  noun: string
} {
  const { filename, name, ext } = track
  const { showWatermarking } = options
  const { isTemp, isOpenable, isPlayable, isPreviewable, subheader, fileType } =
    getTrackStatusDetails(track, options)
  let bar = null
  // example: Untiled track
  let noun = getNounForExt(ext)
  let line1 = name || filename || `Untitled ${noun}`

  if (!track.id && !track.importing && track.progress < 100) {
    bar = (
      <div className="ShortlistTrack_Upload">
        <div
          className="ShortlistTrack_UploadProgress"
          style={{
            width: `${100 - track.progress}%`,
          }}
        />
      </div>
    )
  }

  let hasWaveform = fileType === AUDIO_FILE
  let line2 = (
    <Flex spacing="small">
      {track.metadata?.['Release Date'] && (
        <PreRelease date={track.metadata?.['Release Date']} pending />
      )}
      {subheader && (
        <Flex.Content>
          <TextOverflow content={subheader} />
        </Flex.Content>
      )}
      {track.native_tags && <ColorIndicators tags={track.native_tags} />}
      {track.watermarking_status && showWatermarking && (
        <div className="TrackStatus_Watermarking">
          <WatermarkingStatusFlag
            watermarking_status={track.watermarking_status}
            type={TRACK}
            short
          />
        </div>
      )}
      {track.origin?.source_library &&
        track.internal_data?.is_aggregated_search_track && (
          <>
            {subheader ? <span>–</span> : ''}
            <Flex.Content>
              <a
                href={track.origin.source_library.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-link"
                onClick={() => {
                  // Properties for event #8 from https://disco-team.atlassian.net/browse/DISCO-5985
                  mixpanel.sendPureEvent('Click link', {
                    'Track ID': track.id,
                    Catalog: track.origin?.source_library?.id,
                    'Catalog Business': track.origin?.business?.id,
                    Type: 'Originating catalog',
                    Context: 'Aggregated search',
                  })
                }}
              >
                <TextOverflow content={track.origin.source_library.name} />
              </a>
            </Flex.Content>
          </>
        )}
    </Flex>
  )
  return {
    bar,
    line1,
    line2,
    isTemp,
    isOpenable,
    isPlayable,
    isPreviewable,
    hasWaveform,
    noun,
  }
}
